import React from 'react';
import { Icon } from '@iconify/react';
import { formatDateDDMMYYYY } from '@/utils/dateUtils';
import { formatPlayerDisplayName } from './KlasemenCard';

export interface LeaderboardPlayerItem {
  position: number;
  playerName: string;
  avatarUrl?: string;
  team?: string;
  role?: string;
  matches: number;
  value: number;
  goals?: number;
  assists?: number;
  saves?: number;
  deltaMmr?: number;
  wins?: number;
  winRate?: number;
}

export type LeaderboardCategoryType =
  | 'top-score'
  | 'top-assist'
  | 'top-mmr'
  | 'top-save'
  | 'klasemen'
  | 'padel-klasemen';

export interface LeaderboardRecapCardProps {
  type?: LeaderboardCategoryType | string;
  eventName?: string;
  eventDateFormatted?: string;
  location?: string;
  sportCategory?: string;
  players?: LeaderboardPlayerItem[];
  bgImage?: string;
  transparent?: boolean;
  className?: string;
}

const DEFAULT_BG_IMAGE = 'https://cdn.sporto.id/bgshare.webp';
const SPORTO_LOGO_URL = 'https://cdn.sporto.id/sporto.svg';

const DUMMY_PLAYERS: Record<LeaderboardCategoryType, LeaderboardPlayerItem[]> = {
  'top-score': [
    { position: 1, playerName: 'Alfir Ramadhan', team: 'Garuda Muda FC', matches: 6, value: 9 },
    { position: 2, playerName: 'Rendra', team: 'Rajawali', matches: 6, value: 7 },
    { position: 3, playerName: 'Yoga', team: 'Senayan', matches: 6, value: 6 },
    { position: 4, playerName: 'Bagas', team: 'Bhayangkara', matches: 5, value: 5 },
    { position: 5, playerName: 'Dimas', team: 'Elang Jaya', matches: 6, value: 4 },
  ],
  'top-assist': [
    { position: 1, playerName: 'Alfir Ramadhan', team: 'Garuda Muda FC', matches: 6, value: 8 },
    { position: 2, playerName: 'Rendra', team: 'Rajawali', matches: 6, value: 6 },
    { position: 3, playerName: 'Yoga', team: 'Senayan', matches: 6, value: 5 },
    { position: 4, playerName: 'Bagas', team: 'Bhayangkara', matches: 5, value: 4 },
    { position: 5, playerName: 'Dimas', team: 'Elang Jaya', matches: 6, value: 3 },
  ],
  'top-mmr': [
    { position: 1, playerName: 'Alfir Ramadhan', team: 'Garuda Muda FC', matches: 6, value: 18.5 },
    { position: 2, playerName: 'Rendra', team: 'Rajawali', matches: 6, value: 14.0 },
    { position: 3, playerName: 'Yoga', team: 'Senayan', matches: 6, value: 11.5 },
    { position: 4, playerName: 'Bagas', team: 'Bhayangkara', matches: 5, value: 8.0 },
    { position: 5, playerName: 'Dimas', team: 'Elang Jaya', matches: 6, value: 6.5 },
  ],
  'top-save': [
    { position: 1, playerName: 'Alfir Ramadhan', team: 'Garuda Muda FC', matches: 6, value: 12 },
    { position: 2, playerName: 'Rendra', team: 'Rajawali', matches: 6, value: 9 },
    { position: 3, playerName: 'Yoga', team: 'Senayan', matches: 6, value: 8 },
    { position: 4, playerName: 'Bagas', team: 'Bhayangkara', matches: 5, value: 6 },
    { position: 5, playerName: 'Dimas', team: 'Elang Jaya', matches: 6, value: 5 },
  ],
  'klasemen': [
    { position: 1, playerName: 'Alfir', matches: 12, value: 148 },
    { position: 2, playerName: 'Rendra', matches: 12, value: 141 },
    { position: 3, playerName: 'Yoga', matches: 12, value: 133 },
    { position: 4, playerName: 'Bagas', matches: 12, value: 128 },
    { position: 5, playerName: 'Dimas', matches: 12, value: 121 },
  ],
  'padel-klasemen': [
    { position: 1, playerName: 'Alfir', matches: 12, value: 148 },
    { position: 2, playerName: 'Rendra', matches: 12, value: 141 },
    { position: 3, playerName: 'Yoga', matches: 12, value: 133 },
    { position: 4, playerName: 'Bagas', matches: 12, value: 128 },
    { position: 5, playerName: 'Dimas', matches: 12, value: 121 },
  ],
};

const getInitials = (name?: string): string => {
  if (!name) return 'P';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2 && parts[0][0] && parts[1][0]) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

export const LeaderboardRecapCard: React.FC<LeaderboardRecapCardProps> = ({
  type = 'top-score',
  eventName = 'LIGA MINI SOCCER',
  eventDateFormatted = '19 Nov 2024',
  location = 'Sporto Arena Senayan',
  sportCategory,
  players,
  bgImage,
  transparent = false,
  className = '',
}) => {
  const activeBgImage = bgImage || DEFAULT_BG_IMAGE;
  const formattedDate = formatDateDDMMYYYY(eventDateFormatted);
  const dateLoc = `${location || 'Arena Senayan'} · ${formattedDate}`;

  const normalizedType: LeaderboardCategoryType =
    type.includes('klasemen') || type.includes('padel') || sportCategory?.toLowerCase() === 'padel'
      ? 'padel-klasemen'
      : type.includes('assist')
      ? 'top-assist'
      : type.includes('mmr')
      ? 'top-mmr'
      : type.includes('save')
      ? 'top-save'
      : 'top-score';

  const list = players && players.length > 0 ? players : DUMMY_PLAYERS[normalizedType];

  const firstPlace = list[0];
  const otherPlaces = list.slice(1, 5); // Rows 2 to 5

  const isRacketKlasemen = normalizedType === 'padel-klasemen';
  const firstPlaceWinRate =
    firstPlace?.winRate ??
    (firstPlace && firstPlace.matches > 0 && firstPlace.wins !== undefined
      ? Math.round((firstPlace.wins / firstPlace.matches) * 100)
      : 0);

  const typeConfig = {
    'top-score': {
      label: 'TOP SKOR',
      unit: 'GOL',
      headerUnit: 'GOL',
      icon: 'ph:soccer-ball-bold',
      formatAvg: (item?: LeaderboardPlayerItem) => {
        if (!item || !item.matches) return `${item?.value || 0} gol`;
        const avg = (item.value / Math.max(1, item.matches)).toFixed(1).replace('.', ',');
        return `${avg} gol / laga`;
      },
      formatValue: (val: number) => String(val),
    },
    'top-assist': {
      label: 'TOP ASSIST',
      unit: 'ASSIST',
      headerUnit: 'ASSIST',
      icon: 'ph:sneaker-move-bold',
      formatAvg: (item?: LeaderboardPlayerItem) => {
        if (!item || !item.matches) return `${item?.value || 0} assist`;
        const avg = (item.value / Math.max(1, item.matches)).toFixed(1).replace('.', ',');
        return `${avg} assist / laga`;
      },
      formatValue: (val: number) => String(val),
    },
    'top-mmr': {
      label: 'TOP MMR',
      unit: 'MMR',
      headerUnit: 'MMR',
      icon: 'ph:trend-up-bold',
      formatAvg: (item?: LeaderboardPlayerItem) => {
        return `${item?.matches || 0} laga dimainkan`;
      },
      formatValue: (val: number) => (val > 0 ? `+${val.toFixed(1)}` : val.toFixed(1)),
    },
    'top-save': {
      label: 'TOP SAVE',
      unit: 'SAVE',
      headerUnit: 'SAVE',
      icon: 'ph:hand-palm-bold',
      formatAvg: (item?: LeaderboardPlayerItem) => {
        if (!item || !item.matches) return `${item?.value || 0} save`;
        const avg = (item.value / Math.max(1, item.matches)).toFixed(1).replace('.', ',');
        return `${avg} save / laga`;
      },
      formatValue: (val: number) => String(val),
    },
    'padel-klasemen': {
      label: 'KLASEMEN AKHIR',
      unit: 'POIN',
      headerUnit: 'POIN',
      icon: 'ph:trophy-bold',
      formatAvg: (item?: LeaderboardPlayerItem) => {
        if (!item || !item.matches) return `${item?.value || 0} poin`;
        const avg = (item.value / Math.max(1, item.matches)).toFixed(1).replace('.', ',');
        return `${item.matches} laga · ${avg} poin / laga`;
      },
      formatValue: (val: number) => String(val),
    },
    'klasemen': {
      label: 'KLASEMEN AKHIR',
      unit: 'POIN',
      headerUnit: 'POIN',
      icon: 'ph:trophy-bold',
      formatAvg: (item?: LeaderboardPlayerItem) => {
        if (!item || !item.matches) return `${item?.value || 0} poin`;
        const avg = (item.value / Math.max(1, item.matches)).toFixed(1).replace('.', ',');
        return `${item.matches} laga · ${avg} poin / laga`;
      },
      formatValue: (val: number) => String(val),
    },
  }[normalizedType];

  return (
    <div
      className={`relative w-[540px] h-[960px] overflow-hidden select-none ${
        transparent ? 'bg-transparent' : 'bg-[#0E1726] shadow-[0_8px_30px_rgba(14,23,38,0.18)]'
      } ${className}`}
      style={{ fontFamily: "'Manrope', sans-serif" }}
    >
      {/* Background Image & Overlay */}
      {!transparent && (
        <>
          <img
            src={activeBgImage}
            alt="Foto latar pertandingan"
            className="absolute inset-0 w-full h-full object-cover object-[50%_35%]"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.25) 30%, rgba(0,0,0,0.65) 60%, rgba(0,0,0,0.92) 100%)',
            }}
          />
        </>
      )}

      {/* Content Container */}
      <div className="absolute inset-0 flex flex-col justify-end p-[28px_24px_24px]">
        {/* Dark Glassmorphic Card Container */}
        <div
          className="relative rounded-[24px] p-[24px_22px_22px] overflow-hidden shadow-2xl"
          style={{
            border: '1px solid rgba(255,255,255,0.08)',
            background: 'linear-gradient(180deg, rgba(20,20,22,0.72) 0%, rgba(12,12,14,0.92) 100%)',
            backdropFilter: 'blur(16px)',
          }}
        >
          {/* Header Info */}
          <div className="flex items-start justify-between gap-[16px] mb-[18px]">
            <div className="flex flex-col gap-[2px] min-w-0">
              <span
                className="text-[12px] font-black tracking-[0.14em] text-[#FC480D] uppercase"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {typeConfig.label}
              </span>
              <h2 className="text-[26px] font-extrabold text-white leading-[30px] tracking-[-0.02em] uppercase truncate">
                {eventName}
              </h2>
              <p className="text-[13px] font-medium text-white/60 truncate mt-0.5">
                {dateLoc}
              </p>
            </div>

            {/* Sport / Type Circular Badge */}
            <div className="w-[42px] h-[42px] rounded-full border border-white/20 bg-white/10 flex items-center justify-center text-white shrink-0 shadow-sm">
              <Icon icon={typeConfig.icon} className="w-[22px] h-[22px] text-white" />
            </div>
          </div>

          {/* Spotlight Box for Rank 1 Player */}
          {firstPlace && (
            <div
              className="rounded-[18px] p-[14px_16px] flex items-center justify-between gap-[14px] mb-[18px]"
              style={{
                background: 'rgba(255, 255, 255, 0.06)',
                border: '1px solid rgba(255, 255, 255, 0.12)',
              }}
            >
              <div className="flex items-center gap-[12px] min-w-0 flex-1">
                {/* Avatar */}
                <div className="shrink-0">
                  {firstPlace.avatarUrl ? (
                    <img
                      src={firstPlace.avatarUrl}
                      alt={firstPlace.playerName}
                      className="w-[52px] h-[52px] rounded-full object-cover border-2 border-white/20 shadow-md"
                    />
                  ) : (
                    <div className="w-[52px] h-[52px] rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-white font-extrabold text-[17px] border-2 border-white/20 shadow-md">
                      {getInitials(firstPlace.playerName)}
                    </div>
                  )}
                </div>

                {/* Name & Subtitle */}
                <div className="min-w-0 flex-1">
                  <h3 className="text-[17px] font-extrabold text-white leading-[22px] truncate">
                    {formatPlayerDisplayName(firstPlace.playerName, 16)}
                  </h3>
                  <p className="text-[12px] font-medium text-white/60 truncate mt-[2px]">
                    {isRacketKlasemen && firstPlace.wins !== undefined
                      ? `${firstPlace.matches} Main · ${firstPlace.wins} Menang · ${firstPlaceWinRate}% WR`
                      : `${firstPlace.team ? `${firstPlace.team} · ` : ''}${typeConfig.formatAvg(firstPlace)}`}
                  </p>
                </div>
              </div>

              {/* Big Stat Value */}
              <div className="text-right shrink-0 flex flex-col items-end">
                <span className="text-[32px] font-black text-white leading-none tracking-tight">
                  {typeConfig.formatValue(firstPlace.value)}
                </span>
                <span className="text-[10px] font-black text-white/65 uppercase tracking-wider mt-[4px]">
                  {typeConfig.unit}
                </span>
              </div>
            </div>
          )}

          {/* Table Rows for Rank 2 - 5 */}
          <div className="flex flex-col gap-[2px]">
            {/* Table Header */}
            <div className="flex items-center gap-[10px] px-[8px] pb-[8px] border-b border-white/10">
              <div
                className="w-[20px] text-[11px] font-bold tracking-[0.1em] text-white/40"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                #
              </div>
              <div
                className="flex-1 text-[11px] font-bold tracking-[0.1em] text-white/40 uppercase"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                PEMAIN
              </div>
              {isRacketKlasemen ? (
                <>
                  <div
                    className="w-[36px] text-center text-[11px] font-bold tracking-[0.1em] text-white/40 uppercase"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    M
                  </div>
                  <div
                    className="w-[36px] text-center text-[11px] font-bold tracking-[0.1em] text-white/40 uppercase"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    W
                  </div>
                  <div
                    className="w-[44px] text-center text-[11px] font-bold tracking-[0.1em] text-white/40 uppercase"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    WR
                  </div>
                  <div
                    className="w-[52px] text-right text-[11px] font-bold tracking-[0.1em] text-white/40 uppercase"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    POIN
                  </div>
                </>
              ) : (
                <>
                  <div
                    className="w-[46px] text-center text-[11px] font-bold tracking-[0.1em] text-white/40 uppercase"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    MAIN
                  </div>
                  <div
                    className="w-[56px] text-right text-[11px] font-bold tracking-[0.1em] text-white/40 uppercase"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {typeConfig.headerUnit}
                  </div>
                </>
              )}
            </div>

            {/* Rows */}
            {otherPlaces.map((row, idx) => {
              const rank = row.position || idx + 2;
              const winRate =
                row.winRate ??
                (row.matches > 0 && row.wins !== undefined
                  ? Math.round((row.wins / row.matches) * 100)
                  : 0);
              const displayName = formatPlayerDisplayName(row.playerName, 14);

              return (
                <div
                  key={`${row.playerName}-${idx}`}
                  className="flex items-center gap-[10px] px-[8px] py-[9px] border-b border-white/5 last:border-b-0"
                >
                  {/* Rank */}
                  <div className="w-[20px] text-[14px] font-extrabold text-white/80">
                    {rank}
                  </div>

                  {/* Player Name + Team Badge */}
                  <div className="flex-1 flex items-center gap-[8px] min-w-0">
                    <span className="text-[14px] font-bold text-white truncate">
                      {displayName}
                    </span>
                    {row.team && (
                      <span className="inline-flex items-center px-[6px] py-[2px] rounded-[5px] bg-white/10 text-[10px] font-semibold text-white/75 truncate max-w-[100px]">
                        {row.team}
                      </span>
                    )}
                  </div>

                  {isRacketKlasemen ? (
                    <>
                      {/* Matches Played (M) */}
                      <div className="w-[36px] text-center text-[13px] font-semibold text-white/70">
                        {row.matches}
                      </div>

                      {/* Wins (W) */}
                      <div className="w-[36px] text-center text-[13px] font-semibold text-white/70">
                        {row.wins ?? 0}
                      </div>

                      {/* Winrate (WR) */}
                      <div className="w-[44px] text-center text-[13px] font-semibold text-white/70">
                        {winRate}%
                      </div>

                      {/* Poin */}
                      <div className="w-[52px] text-right text-[15px] font-black text-white">
                        {typeConfig.formatValue(row.value)}
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Matches Played */}
                      <div className="w-[46px] text-center text-[14px] font-semibold text-white/70">
                        {row.matches}
                      </div>

                      {/* Stat Value */}
                      <div className="w-[56px] text-right text-[15px] font-black text-white">
                        {typeConfig.formatValue(row.value)}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom Logo */}
        <div className="flex justify-center pt-[20px]">
          <img src={SPORTO_LOGO_URL} alt="Sporto" className="h-[24px] w-auto object-contain block opacity-95" />
        </div>
      </div>
    </div>
  );
};

export default LeaderboardRecapCard;
